// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AskV2ForYou } from "./AskV2ForYou";
import { AskV2Hero } from "./AskV2Hero";
import { AskV2MentionTasks } from "./AskV2MentionTasks";
import { type AskV2ComposerMode } from "./ComposerModeToggle";
import { useAskV2HomeCopy } from "./useAskV2HomeCopy";
import { WelcomeBriefing } from "./WelcomeBriefing";

import { HomeProjectPicker } from "../../components/ask-puntt-home";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { invalidateAskPunttUnreadLists } from "../../utils/queries/askPuntt";
import { useStartNewProjectChat } from "../../utils/useStartNewProjectChat";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_CHAT_PATH, ASK_V2_PROJECT_PATH } from "../paths";
import { usePostToAskV2ProjectSharedFeed } from "../services/postToAskV2ProjectSharedFeed";
import { createAskV2Project } from "../services/projects";
import { AskV2ChatFileDrop } from "../transport/AskV2ChatFileDrop";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { useAskV2Transport } from "../transport/useAskV2Transport";

const HOME_PROJECT_LANE_SPACER_CLASS = "h-10";
const PROJECT_LANE_EXIT_MS = 220;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function AskV2Home() {
  const transport = useAskV2Transport();
  const postToProjectFeed = usePostToAskV2ProjectSharedFeed();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics("ask_puntt_v2_home");
  const homeCopy = useAskV2HomeCopy();
  const navigatedToChatRef = useRef(false);
  const [composerMode, setComposerMode] = useState<AskV2ComposerMode>("chat");
  // why: the create ACK flips transport.chatId to non-null one commit BEFORE
  // the navigate effect can run (effects fire after paint). Deriving the
  // hero-hide from a useState set inside that effect leaves a single painted
  // frame where chatId is set but the flag is still false — the landing hero
  // flashes back over the thread we're about to route to. Latch the exit
  // synchronously during render instead: once this home has a thread AND (a
  // chatId has arrived OR chat-mode navigation already began), the hero stays
  // hidden for that same commit and every commit until this component unmounts.
  const exitingToChatRef = useRef(false);
  if (
    transport.messages.length > 0 &&
    (transport.chatId != null || navigatedToChatRef.current) &&
    composerMode !== "project"
  ) {
    exitingToChatRef.current = true;
  }
  // why: read the latch FIRST, not AND-ed with a live `messages.length`. That
  // conjunction handed the one-way guarantee back: the first history payload
  // for the chat the ACK just created runs through syncFromServer, which keeps
  // only `failed`/`sending` local rows — the ACK's `sent` user row and
  // `streaming` assistant row are both dropped — so `transport.messages`
  // empties whenever that payload has no visible rows yet (unmapped/hidden
  // rows, or a read that does not see the turn yet). navigate() is async, so
  // this home is still mounted while the chat route loads, and the landing
  // hero repainted over the thread we are routing to. Pre-ACK (chatId still
  // null) the thread is shown off the optimistic rows alone.
  const showPreNavigateThread =
    exitingToChatRef.current ||
    (transport.messages.length > 0 && transport.chatId == null);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(
    null,
  );
  const [selectedSharedChatId, setSelectedSharedChatId] = useState<
    string | null
  >(null);
  const [projectLaneMounted, setProjectLaneMounted] = useState(false);
  const [projectLaneExiting, setProjectLaneExiting] = useState(false);
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const projectLaneExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // why: home composer used to open a name modal; christo retitles "New project" from the first message
  const { startNewProjectChat } = useStartNewProjectChat(
    "ask_puntt_v2_home",
    (project) => {
      setSelectedProjectId(project.id);
      setSelectedProjectName(project.name);
      setSelectedSharedChatId(project.sharedChatId);
    },
    createAskV2Project,
  );

  useEffect(() => {
    const chatId = transport.chatId;
    if (!chatId || navigatedToChatRef.current || composerMode === "project") {
      return;
    }
    // why: the chat route mounts its own transport, so its first render picks
    // between the thread and ask-v2-thread-loading purely on the history query.
    // Routing the instant the create ACK lands sends it there mid-fetch, so the
    // grey bars replace the messages this page is still painting — the thread
    // above exists only because the ACK is optimistic, and the fresh transport
    // on the other side has nothing to fall back on. Hold the route until that
    // payload is cached; the chat route then paints the same rows from cache and
    // the handoff is invisible instead of a skeleton flash over live messages.
    if (transport.historyLoading) return;
    navigatedToChatRef.current = true;
    invalidateAskPunttUnreadLists(queryClient);
    void navigate({
      to: ASK_V2_CHAT_PATH,
      params: { chatId },
      replace: true,
    });
  }, [
    composerMode,
    navigate,
    queryClient,
    transport.chatId,
    transport.historyLoading,
  ]);

  useEffect(() => {
    if (composerMode === "project") {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
      setProjectLaneMounted(true);
      setProjectLaneExiting(false);
      return;
    }
    if (!projectLaneMounted) return;
    if (prefersReducedMotion()) {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      return;
    }
    setProjectLaneExiting(true);
    projectLaneExitTimerRef.current = setTimeout(() => {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      projectLaneExitTimerRef.current = null;
    }, PROJECT_LANE_EXIT_MS);
    return () => {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
    };
  }, [composerMode, projectLaneMounted]);

  const needsProjectSelection =
    composerMode === "project" && selectedProjectId == null;

  const handleProjectSubmit = async (input: {
    message: string;
    attachments: import("../services/chat").AskV2Attachment[];
    webSearch: boolean;
  }): Promise<boolean> => {
    if (needsProjectSelection || projectSubmitting) return false;
    if (!selectedProjectId) return false;

    const submittedMessage = input.message;
    setProjectSubmitting(true);
    try {
      // why: home project-mode submit has no thread to highlight, and this
      // branch renders no TextSelectionToolbar (the landing SummonBar never
      // wires onAddQuotedContext), so transport.quotedContexts is always empty
      // here — forwarding it was dead code. Quoting is a chat-page / project-
      // page surface; do not claim it on the home landing.
      await postToProjectFeed.mutateAsync({
        projectId: selectedProjectId,
        sharedChatId: selectedSharedChatId,
        message: input.message,
        attachments:
          input.attachments.length > 0 ? input.attachments : undefined,
        ...(input.webSearch ? { webSearch: true } : {}),
      });
      // why: a project post appends a turn to the project's SHARED chat, so it
      // moves that row's recency/unread state exactly like the create path
      // does — but usePostToAskV2ProjectSharedFeed only invalidates the feed,
      // timeline and that one chat's history (the V1 hook it was ported from
      // also invalidated the chat lists). Without this the sidebar recents and
      // the project list keep a stale row for up to the 30s unread poll after
      // we navigate onto the project we just posted into.
      invalidateAskPunttUnreadLists(queryClient);
      analytics.capture("ask_puntt_chat_created", {
        workflow_type: "general",
        message_length: input.message.length,
        has_attachments: input.attachments.length > 0,
        attachment_count: input.attachments.length,
        web_search: input.webSearch,
        project_id: selectedProjectId,
        project_chat_kind: "shared",
      });
      if (transport.composer === submittedMessage) {
        transport.setComposer("");
      }
      transport.clearQuotedContexts();
      if (transport.webSearch === input.webSearch) {
        transport.setWebSearch(false);
      }
      void navigate({
        to: ASK_V2_PROJECT_PATH,
        params: { projectId: selectedProjectId },
      });
      return true;
    } catch (error) {
      analytics.capture("ask_puntt_chat_create_error", {
        workflow_type: "general",
        has_attachments: input.attachments.length > 0,
        attachment_count: input.attachments.length,
        web_search: input.webSearch,
        project_id: selectedProjectId,
        ...errorAnalyticsPayload(error),
      });
      return false;
    } finally {
      setProjectSubmitting(false);
    }
  };

  // why: omitted canInteract fails open; only an explicit false is a read-only chat that must ignore drops
  const fileDropDisabled = transport.historyChat?.canInteract === false;

  if (showPreNavigateThread) {
    return (
      <AskV2ChatFileDrop disabled={fileDropDisabled}>
        <AskV2ConversationShell
          testId="ask-v2-home"
          header={null}
          scrollResetKey={transport.chatId ?? "home-draft"}
          scrollDeps={[transport.displayMessages]}
          autoScrollEnabled={!transport.inFlight}
          // why: this is the one shell a Home send lands in — `showPreNavigateThread`
          // flips true on the optimistic row (messages > 0, chatId still null), so
          // the mount itself is the rising edge the send signal exists for. See
          // `scrollSentSignal` on AskV2ConversationShellProps.
          scrollSentSignal={transport.sending}
          // why: neither Home composer wires mentionMembers /
          // enableDirectoryMentions. An @mention grants the mentioned person
          // access to the chat when the owner sends, and this thread's chat is
          // not created server-side yet — there is no owner and no roster to
          // scope a mention against. AskV2ChatPage / AskV2ProjectPage are the
          // surfaces that opt in, and they gate the enterprise directory on chat
          // ownership. Pinned by "keeps both Home composers off the @mention
          // directory path".
          composer={<AskV2SummonBar transport={transport} />}
        >
          <AskV2Thread transport={transport} />
        </AskV2ConversationShell>
      </AskV2ChatFileDrop>
    );
  }

  return (
    <AskV2ChatFileDrop disabled={fileDropDisabled}>
      <div
        data-testid="ask-v2-home"
        className="ask-puntt-surface relative flex size-full min-h-0 flex-col overflow-hidden"
      >
        <div
          className="mx-auto flex size-full min-h-0 max-w-[900px] flex-1 flex-col overflow-y-auto px-4"
          data-testid="ask-v2-home-main-column"
        >
          <div
            className="min-h-0 flex-1"
            aria-hidden="true"
            data-testid="ask-v2-home-top-flex-spacer"
          />
          <AskV2Hero
            headline={homeCopy.copy?.headline ?? null}
            accentWord={homeCopy.copy?.accentWord ?? null}
          />
          <div className="relative z-10 w-full shrink-0">
            <div className="relative">
              <AskV2SummonBar
                landing
                transport={transport}
                composerMode={composerMode}
                onComposerModeChange={setComposerMode}
                needsProjectSelection={needsProjectSelection}
                projectSubmitting={projectSubmitting}
                onProjectSubmit={handleProjectSubmit}
              />
              {projectLaneMounted ? (
                <div
                  className={
                    projectLaneExiting
                      ? "animate-ask-puntt-home-project-lane-out absolute inset-x-0 top-full z-10 origin-top"
                      : "animate-ask-puntt-home-project-lane-in absolute inset-x-0 top-full z-10 origin-top"
                  }
                  data-testid="ask-v2-home-project-lane"
                  data-exiting={projectLaneExiting ? "true" : "false"}
                  onAnimationEnd={() => {
                    if (!projectLaneExiting) return;
                    if (projectLaneExitTimerRef.current != null) {
                      clearTimeout(projectLaneExitTimerRef.current);
                      projectLaneExitTimerRef.current = null;
                    }
                    setProjectLaneMounted(false);
                    setProjectLaneExiting(false);
                  }}
                >
                  <HomeProjectPicker
                    testIdPrefix="ask-v2-home"
                    selectedProjectId={selectedProjectId}
                    selectedProjectName={selectedProjectName}
                    onSelect={(project) => {
                      setSelectedProjectId(project.id);
                      setSelectedProjectName(project.name);
                      setSelectedSharedChatId(project.sharedChatId);
                    }}
                    onClear={() => {
                      setSelectedProjectId(null);
                      setSelectedProjectName(null);
                      setSelectedSharedChatId(null);
                    }}
                    onNewProject={() => {
                      void startNewProjectChat("home_project_picker");
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <div
            className="flex flex-col items-center gap-6 py-8"
            data-testid="ask-v2-home-below-composer"
          >
            {projectLaneMounted ? (
              <div
                aria-hidden="true"
                data-testid="ask-v2-home-project-lane-spacer"
                className={HOME_PROJECT_LANE_SPACER_CLASS}
              />
            ) : null}
            <AskV2MentionTasks />
            <AskV2ForYou />
            <WelcomeBriefing
              pending={homeCopy.pending}
              lead={homeCopy.copy?.subheading ?? null}
              disabled={
                transport.sending ||
                transport.inFlight ||
                projectSubmitting ||
                needsProjectSelection
              }
              onSelect={(prompt) => {
                if (composerMode === "project") {
                  void handleProjectSubmit({
                    message: prompt,
                    attachments: [],
                    webSearch: transport.webSearch,
                  });
                  return;
                }
                transport.setComposer(prompt);
                void transport.send();
              }}
            />
          </div>
          <div className="min-h-0 flex-1" aria-hidden="true" />
        </div>
      </div>
    </AskV2ChatFileDrop>
  );
}
